// src/ConsumerApp/api/wallet/wallet.hooks.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WalletService } from './wallet.service';

/**
 * Hook to fetch the current wallet balance for a customer.
 */
export const useGetWalletBalance = (customerId) => {
  return useQuery({
    queryKey: ['walletBalance', customerId],
    queryFn: async () => {
      const response = await WalletService.getWalletBalance(customerId);
      return response?.data || response;
    },
    enabled: Boolean(customerId),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * Hook to fetch the transaction statement / history of the wallet.
 */
export const useGetWalletHistory = (customerId, page = 1, pageSize = 20) => {
  return useQuery({
    queryKey: ['walletHistory', customerId, page, pageSize],
    queryFn: async () => {
      const response = await WalletService.getWalletHistory(customerId, page, pageSize);
      return response?.data || response;
    },
    enabled: Boolean(customerId),
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });
};

/**
 * Hook to apply wallet balance to an order or bucket.
 */
export const useApplyWalletBalance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => WalletService.applyWalletBalance(payload),
    onSuccess: (data, variables) => {
      const customerId = variables?.customer_id;
      if (customerId) {
        queryClient.invalidateQueries({ queryKey: ['walletBalance', customerId] });
        queryClient.invalidateQueries({ queryKey: ['walletHistory', customerId] });
        queryClient.invalidateQueries({ queryKey: ['bucketSummary', customerId] });
        queryClient.invalidateQueries({ queryKey: ['bucketDetails', customerId] });
      }
    },
  });
};